import React, { useState, useEffect } from 'react';
import { Grid, Card, CardContent, Typography, Box, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Slider, Stack, CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import { formatCurrency } from '../../utils/formatters';
import { getCommissions, updateCommissionSettings, getCommissionOverrides } from '../../api/endpoints/commissions';

const CommissionSettings = () => {
  const [commissions, setCommissions] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatPercent, setChatPercent] = useState(20);
  const [callPercent, setCallPercent] = useState(15);
  const [videoPercent, setVideoPercent] = useState(25);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [commRes, overRes] = await Promise.all([
        getCommissions({ limit: 50 }),
        getCommissionOverrides(),
      ]);
      setCommissions(commRes.data.data || []);
      setOverrides(overRes.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load commission data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await updateCommissionSettings({ chat: chatPercent, call: callPercent, video: videoPercent });
      toast.success('Commission settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save settings');
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <PageHeader title="Commission Settings" subtitle="Manage platform commission rates and overrides" />
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Default Commission Rates</Typography>
              <Stack spacing={3} sx={{ mt: 2 }}>
                <Box>
                  <Typography gutterBottom>Chat: {chatPercent}%</Typography>
                  <Slider value={chatPercent} onChange={(e, v) => setChatPercent(v)} min={0} max={50} />
                </Box>
                <Box>
                  <Typography gutterBottom>Call: {callPercent}%</Typography>
                  <Slider value={callPercent} onChange={(e, v) => setCallPercent(v)} min={0} max={50} />
                </Box>
                <Box>
                  <Typography gutterBottom>Video Call: {videoPercent}%</Typography>
                  <Slider value={videoPercent} onChange={(e, v) => setVideoPercent(v)} min={0} max={50} />
                </Box>
                <Button variant="contained" onClick={handleSave}>Save Settings</Button>
              </Stack>
            </CardContent>
          </Card>
          {overrides.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>Astrologer Overrides</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Astrologer</TableCell>
                        <TableCell align="right">Override %</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {overrides.map((o, i) => (
                        <TableRow key={i}>
                          <TableCell><Typography variant="body2">{o.name || o.astrologerId}</Typography></TableCell>
                          <TableCell align="right">{o.commission}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Recent Commissions</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Astrologer</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Admin</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {commissions.slice(0, 20).map((c, i) => (
                      <TableRow key={c._id || i}>
                        <TableCell><Typography variant="body2">{c.astrologerId?.name || '-'}</Typography></TableCell>
                        <TableCell align="right">{formatCurrency(c.totalAmount)}</TableCell>
                        <TableCell align="right">{formatCurrency(c.adminCommission)}</TableCell>
                        <TableCell><Typography variant="caption" color={c.status === 'settled' ? 'success.main' : 'warning.main'}>{c.status}</Typography></TableCell>
                      </TableRow>
                    ))}
                    {commissions.length === 0 && (
                      <TableRow><TableCell colSpan={4}><Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>No commissions yet</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CommissionSettings;
